'use client';

import { useState, type ReactNode } from 'react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import EditorialHeader from '@/components/shared/EditorialHeader';
import EditorialFooter from '@/components/shared/EditorialFooter';
import WaitlistModal from '@/components/landing/WaitlistModal';
import { LANDING_COLORS } from '@/constants/landing-colors';

const variants: LandingVariant[] = ['jams', 'escuelas', 'salas'];
type LandingVariant = 'jams' | 'escuelas' | 'salas';
const links = [
  { id: 'legal', label: 'Aviso legal' },
  { id: 'privacidad', label: 'Privacidad' },
  { id: 'cookies', label: 'Cookies' },
  { id: 'terminos', label: 'Términos' },
  { id: 'cancelaciones', label: 'Cancelaciones' },
];

export function useLegalNavigation(defaultVariant: LandingVariant = 'jams') {
  const params = useSearchParams();
  const from = params.get('from') as LandingVariant;
  const variant = variants.includes(from) ? from : defaultVariant;
  const href = (path: string) => path.startsWith('/') ? `${path}${path.includes('?') ? '&' : '?'}from=${variant}` : path;
  return { variant, href };
}

export default function LegalSiteLayout({ active, defaultVariant = 'jams', children }: {
  active: 'legal' | 'privacidad' | 'cookies' | 'terminos' | 'cancelaciones';
  defaultVariant?: LandingVariant;
  children: ReactNode;
}) {
  const { href } = useLegalNavigation(defaultVariant);
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  return <div className="min-h-screen bg-white text-zinc-900">
    <EditorialHeader variant="jams" ctaLabel="Apuntarme a la beta" onCtaClick={() => setWaitlistOpen(true)} linkToHome showLocalNavigation={false} />
    <main className="mx-auto max-w-4xl px-5 pb-20 pt-32 sm:px-8 sm:pt-36">
      <nav aria-label="Información legal" className="mb-10 flex flex-wrap gap-3 text-sm">
        {links.map(link => <Link key={link.id} href={href(`/${link.id}`)} aria-current={active === link.id ? 'page' : undefined} className={`rounded-lg border px-4 py-2 ${active === link.id ? 'border-zinc-900 bg-zinc-900 text-white' : 'border-zinc-200 hover:bg-zinc-50'}`}>{link.label}</Link>)}
      </nav>
      {children}
    </main>
    <EditorialFooter variant="jams" actionLabel="Apuntarme a la beta" onAction={() => setWaitlistOpen(true)} />
    <WaitlistModal open={waitlistOpen} onOpenChange={setWaitlistOpen} colors={LANDING_COLORS.jams} source="legal_footer" />
  </div>;
}
